// This is a wrapper around MaterialReactTable that disables memoization 
// until MRT is compatible with React Compiler

import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import { visuallyHidden } from "@mui/utils";
import { alpha, styled } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import type { TableProps } from "@mui/material/Table";
import type { TableContainerProps } from "@mui/material/TableContainer";
import type { TableCellProps } from "@mui/material/TableCell";
import type { TextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import {
  getIsRowSelected,
  getMRT_RowSelectionHandler,
  getMRT_SelectAllHandler,
  MaterialReactTable,
  type MRT_Cell,
  type MRT_Column,
  type MRT_Row,
  type MRT_RowData,
  type MRT_TableInstance,
  type MRT_TableOptions,
  useMaterialReactTable,
} from "material-react-table";
import type { ChangeEvent, MouseEvent } from "react";
import { RowActionsColumnHeader } from "_shared/_views/tables/row-actions";

const legacyTextFieldRootProps = new Set([
  "as",
  "FormHelperTextProps",
  "InputLabelProps",
  "InputProps",
  "ownerState",
  "SelectProps",
  "sx",
  "theme",
  "inputProps",
]);

export const MrtTextFieldRoot = styled(FormControl, {
  shouldForwardProp: (prop) => !legacyTextFieldRootProps.has(prop.toString()),
})({});

type InputSlotProps = Record<string, unknown> | ((ownerState: unknown) => Record<string, unknown>);
type LegacyCheckboxProps = {
  inputProps?: Record<string, unknown>;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  slotProps?: { input?: InputSlotProps; [key: string]: unknown };
  sx?: unknown;
  title?: string;
  [key: string]: unknown;
};

function withMrtTextFieldRootSlot<TProps extends TextFieldProps>(textFieldProps: TProps | undefined): TProps {
  return {
    ...textFieldProps,
    slots: {
      ...textFieldProps?.slots,
      root: textFieldProps?.slots?.root ?? MrtTextFieldRoot,
    },
  } as TProps;
}

function parseFromValuesOrFunc<TArgs, TValue>(
  valueOrFunc: TValue | ((args: TArgs) => TValue) | undefined,
  args: TArgs,
): TValue | undefined {
  return typeof valueOrFunc === "function" ? (valueOrFunc as (args: TArgs) => TValue)(args) : valueOrFunc;
}

function mergeInputSlotProps(
  inputSlotProps: InputSlotProps | undefined,
  inputAriaLabel: string,
  legacyInputProps: Record<string, unknown> | undefined,
): InputSlotProps {
  const mergedInputSlotProps =
    typeof inputSlotProps === "function"
      ? (ownerState: unknown) => ({
          "aria-label": inputAriaLabel,
          ...legacyInputProps,
          ...inputSlotProps(ownerState),
        })
      : {
          "aria-label": inputAriaLabel,
          ...legacyInputProps,
          ...inputSlotProps,
        };

  return mergedInputSlotProps;
}

const HiddenHeaderLabel = styled("span")({ ...visuallyHidden });

/** The row-select column's header where there is no select-all to draw. See its use below. */
function RowSelectColumnHeader() {
  return <HiddenHeaderLabel>Select</HiddenHeaderLabel>;
}

type MrtSelectCheckboxProps<TData extends MRT_RowData> = {
  row?: MRT_Row<TData>;
  staticRowIndex?: number;
  table: MRT_TableInstance<TData>;
};

function MrtSelectCheckbox<TData extends MRT_RowData>(props: Readonly<MrtSelectCheckboxProps<TData>>) {
  "use no memo";

  const { row, staticRowIndex, table } = props;
  const {
    getState,
    options: {
      enableMultiRowSelection,
      localization,
      muiSelectAllCheckboxProps,
      muiSelectCheckboxProps,
      selectAllMode,
    },
  } = table;
  const { density, isLoading } = getState();
  const selectAll = !row;

  const allRowsSelected = selectAll
    ? selectAllMode === "page"
      ? table.getIsAllPageRowsSelected()
      : table.getIsAllRowsSelected()
    : undefined;
  const isChecked = selectAll ? allRowsSelected : getIsRowSelected({ row, table });
  const inputAriaLabel = selectAll ? localization.toggleSelectAll : localization.toggleSelectRow;

  const rawCheckboxProps = (
    selectAll
      ? parseFromValuesOrFunc(muiSelectAllCheckboxProps, { table })
      : parseFromValuesOrFunc(muiSelectCheckboxProps, {
          row,
          staticRowIndex,
          table,
        })
  ) as LegacyCheckboxProps | undefined;

  const {
    inputProps: legacyInputProps,
    onClick,
    slotProps,
    sx,
    title,
    ...checkboxProps
  } = rawCheckboxProps ?? {};

  const onSelectionChange = row
    ? getMRT_RowSelectionHandler({
        row,
        staticRowIndex,
        table,
      })
    : undefined;
  const onSelectAllChange = getMRT_SelectAllHandler({ table });

  const commonProps = {
    "aria-label": inputAriaLabel,
    checked: isChecked,
    disabled: isLoading || (row && !row.getCanSelect()) || row?.id === "mrt-row-create",
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (selectAll) {
        onSelectAllChange(event);
      } else {
        onSelectionChange?.(event);
      }
    },
    size: (density === "compact" ? "small" : "medium") as "medium" | "small",
    ...checkboxProps,
    onClick: (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onClick?.(event);
    },
    slotProps: {
      ...slotProps,
      input: mergeInputSlotProps(slotProps?.input, inputAriaLabel, legacyInputProps),
    },
    sx: (theme: Theme) => ({
      height: density === "compact" ? "1.75rem" : "2.5rem",
      m: density !== "compact" ? "-0.4rem" : undefined,
      width: density === "compact" ? "1.75rem" : "2.5rem",
      zIndex: 0,
      ...(parseFromValuesOrFunc(sx, theme) as object),
    }),
    title: undefined,
  };

  return (
    <Tooltip title={title ?? inputAriaLabel}>
      {enableMultiRowSelection === false ? (
        <Radio {...commonProps} />
      ) : (
        <Checkbox
          indeterminate={
            !isChecked && selectAll ? table.getIsSomeRowsSelected() : row?.getIsSomeSelected() && row.getCanSelectSubRows()
          }
          {...commonProps}
        />
      )}
    </Tooltip>
  );
}

type MRTNoMemoProps<TData extends MRT_RowData> = {
  options?: MRT_TableOptions<TData>;
};

type MrtBodyCellArgs<TData extends MRT_RowData> = {
  cell: MRT_Cell<TData>;
  column: MRT_Column<TData>;
  row: MRT_Row<TData>;
  table: MRT_TableInstance<TData>;
};
type MrtSelectCellArgs<TData extends MRT_RowData> = {
  row: MRT_Row<TData>;
  staticRowIndex?: number;
  table: MRT_TableInstance<TData>;
};

/**
 * Keeps the actions column to the width of its controls, and centres them in it.
 *
 * Two layouts need two answers. A `grid` table sizes its columns from MRT's own `size`/`grow`, so
 * the tables using it already pin their own width. A `semantic` one — the default — is a real
 * `<table>` under `table-layout: auto`, where MRT's sizing options do nothing and the browser hands
 * the column a share of whatever is left over: measured before this, 280px of column around 120px of
 * buttons on the fee schedules page, and 152px around a single 32px menu on marketplace profiles.
 * A tiny declared width is the standard way to tell the auto algorithm to shrink a column to its
 * content and give the slack to the columns that hold the data.
 *
 * `align` alone does not centre the controls: the row's container is a block-level flex box, so it
 * fills the cell and packs its buttons at the start regardless. The children have to be told.
 *
 * Column-level cell props *replace* the table's own for that column rather than adding to them, so
 * a table that paints its rows — the redline tables shade a resolved-and-accepted row green across
 * every cell — would lose that paint on this one column. The table's props are read here and kept
 * underneath, with the width and centring layered on top.
 */
/** The actions column, sized to its controls and no wider. */
const ROW_ACTIONS_COLUMN_ID = "mrt-row-actions";
const oneCollapsedButton = 48;

/**
 * How the actions column is presented, layered onto whatever the table already says about its
 * cells rather than replacing it.
 *
 * Two layouts need two answers. A `grid` table sizes its columns from MRT's own `size`/`grow`, so
 * the tables using it already pin their own width. A `semantic` one — the default — is a real
 * `<table>` under `table-layout: auto`, where MRT's sizing options do nothing and the browser hands
 * the column a share of whatever is left over: measured before this, 280px of column around 120px of
 * buttons on the fee schedules page, and 152px around a single 32px menu on marketplace profiles.
 * A tiny declared width is the standard way to tell the auto algorithm to shrink a column to its
 * content and give the slack to the columns that hold the data.
 *
 * `align` alone does not centre the controls: the row's container is a block-level flex box, so it
 * fills the cell and packs its buttons at the start regardless. The children have to be told.
 */
function rowActionsCellSx(layoutMode: MRT_TableOptions<MRT_RowData>["layoutMode"]) {
  const shrinkToContent = layoutMode === "grid" ? {} : { width: "1%", whiteSpace: "nowrap" as const };
  return {
    px: 0.5,
    minWidth: oneCollapsedButton,
    ...shrinkToContent,
    "& > *": { justifyContent: "center" },
    // MRT's own menu button carries a 10px left margin, which pushes it off centre by five.
    "& > .MuiIconButton-root": { marginLeft: 0, marginRight: 0 },
  };
}

/**
 * Adds that presentation to the table's own body-cell props, for the actions column alone.
 *
 * This is deliberately not a column-level `muiTableBodyCellProps`: those *replace* the table's for
 * the column they name, and the tables that paint their rows — the redline tables shade a
 * resolved-and-accepted row green across every cell — would lose the paint on this one column.
 * Extending the table-level callback keeps every such rule and adds the width and centring after it.
 */
function withRowActionsCellProps<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  const tableCellProps = baseOptions.muiTableBodyCellProps;
  return (cellArgs: MrtBodyCellArgs<TData>): TableCellProps => {
    const inherited = (typeof tableCellProps === "function" ? tableCellProps(cellArgs) : tableCellProps) ?? {};
    if (cellArgs.column.id !== ROW_ACTIONS_COLUMN_ID) return inherited;
    // One object, not an array: MRT spreads `sx` into its own, and a spread array becomes
    // `{0: …, 1: …}` — which is how the actions cell silently lost every style the table gave it.
    const inheritedSx = typeof inherited.sx === "function" ? inherited.sx : (inherited.sx ?? {});
    return {
      ...inherited,
      align: "center" as const,
      sx: ((theme: Theme) => ({
        ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
        ...rowActionsCellSx(baseOptions.layoutMode),
      })) as TableCellProps["sx"],
    };
  };
}

/**
 * The same, for the actions column's header.
 *
 * A column-level `muiTableHeadCellProps` has exactly the replacing problem described above, and the
 * team roster is the case that shows it: that table paints the pinned actions header with the
 * theme's own paper so the column it floats over cannot read through it, and a column-level
 * override drops that paint back to `inherit` — "Role" renders as "R le" behind the icons (#4955).
 */
function withRowActionsHeadCellProps<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  const headCellProps = baseOptions.muiTableHeadCellProps;
  return (headArgs: { column: MRT_Column<TData>; table: MRT_TableInstance<TData> }): TableCellProps => {
    const inherited = parseFromValuesOrFunc(headCellProps, headArgs) ?? {};
    if (headArgs.column.id !== ROW_ACTIONS_COLUMN_ID) return inherited;
    const inheritedSx = inherited.sx ?? {};
    return {
      ...inherited,
      align: "center" as const,
      sx: ((theme: Theme) => ({
        ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
        ...rowActionsCellSx(baseOptions.layoutMode),
      })) as TableCellProps["sx"],
    };
  };
}

/**
 * Lets a table with no rows take the height of the pane it is drawn in, so the message that stands
 * in for the rows is centred in that pane rather than tucked under the header.
 *
 * This is only ever in play while the table is empty. It changes the height of the one row MRT
 * draws in place of the rows, and while there are rows there is nothing here to distort.
 *
 * The two layout modes need two answers, and both were measured. A `semantic` table is a real
 * `<table>`: a height on it goes to the body row, and `vertical-align: middle` — which a `<td>` has
 * of its own accord — centres the message in it, with the header row keeping its own 52px. A `grid`
 * table is a CSS grid whose tracks all stretch to share out the slack, and the header grew from
 * 52px to 181px doing exactly that, so its tracks are named instead: the head takes what it needs
 * and the body takes the rest.
 *
 * Where the pane has no height of its own to give — a table that is as tall as its content — none
 * of this resolves to anything and nothing moves. See
 * [table empty states](../../../../../docs/software-design/table-empty-states.md).
 */
function emptyTableFillsThePane<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  return {
    height: "100%",
    // The cell that holds the message is not a row and should not be dressed as one: MRT's own
    // padding pushes the message off centre by half of it, and the bottom border draws a rule
    // across the middle of an otherwise empty pane, which reads as a row that failed to render.
    "& > tbody > tr > td": { border: "none", padding: 0 },
    ...(baseOptions.layoutMode?.startsWith("grid")
      ? { gridTemplateRows: baseOptions.enableTableHead === false ? "1fr" : "auto 1fr" }
      : {}),
  };
}

/**
 * Adds that height to the table's own props, for the renders where the table has no rows.
 *
 * The creating row counts as a row: MRT draws it in the body, and stretching the body around a real
 * row is the one thing this is not for.
 */
function withEmptyTableProps<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  const tableProps = baseOptions.muiTableProps;
  return (tableArgs: { table: MRT_TableInstance<TData> }): TableProps => {
    const inherited = parseFromValuesOrFunc(tableProps, tableArgs) ?? {};
    const isEmpty = !tableArgs.table.getRowModel().rows.length && !tableArgs.table.getState().creatingRow;
    if (!isEmpty) return inherited;
    const inheritedSx = inherited.sx ?? {};
    return {
      ...inherited,
      sx: ((theme: Theme) => ({
        ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
        ...emptyTableFillsThePane(baseOptions),
      })) as TableProps["sx"],
    };
  };
}

/**
 * A scroll handle the reader can actually see, for a table whose columns run past its pane.
 *
 * Every one of these tables is drawn in a panel narrower than its columns, so the rows continue past
 * the right-hand edge — and until now the only thing that said so was a pair of chevrons this app drew
 * for itself, stacked under the paginator's own chevrons where the table had one (#4955). A real
 * scrollbar says it better and says it in the place a reader already looks.
 *
 * It has to be styled rather than left to the platform — see the rule below for which engine takes
 * which half of it, and why they cannot both be given.
 *
 * It appears when there IS overflow and not otherwise, because the container scrolls on `auto`: a
 * permanently drawn empty trough under a table that fits says there is somewhere to go when there is
 * not.
 *
 * A capture cannot photograph one without help: Playwright passes `--hide-scrollbars` to every
 * headless Chromium of its own accord, so a spec meaning to show a scrolling surface has to drop it
 * through `launchOptions.ignoreDefaultArgs`.
 */
export function visibleScrollbarStyles(theme: Theme) {
  const trough = alpha(theme.palette.common.black, 0.06);
  const handle = alpha(theme.palette.common.black, 0.32);
  return {
    /**
     * The two families are mutually exclusive, and that is not a style preference.
     *
     * Chromium has ignored every `::-webkit-scrollbar-*` rule on an element that also carries
     * `scrollbar-width` or `scrollbar-color` since M121 — and the standard properties alone leave the
     * scrollbar an OVERLAY one, which Windows 11 and macOS both hide until it is already moving. The
     * two together therefore produce exactly the scrollbar this rule exists to replace. Styling the
     * pseudo-elements is what turns an overlay scrollbar into one that is laid out and always drawn
     * while the container overflows, so the engines that have them are given only those; Firefox,
     * which has neither the pseudo-elements nor the ignore rule, is given the standard properties.
     */
    "@supports not selector(::-webkit-scrollbar)": {
      scrollbarWidth: "thin" as const,
      scrollbarColor: `${handle} ${trough}`,
    },
    "&::-webkit-scrollbar": { WebkitAppearance: "none", height: 10, width: 10 },
    "&::-webkit-scrollbar-track": { backgroundColor: trough, borderRadius: 5 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: handle,
      backgroundClip: "content-box",
      border: "2px solid transparent",
      borderRadius: 5,
      "&:hover": { backgroundColor: alpha(theme.palette.common.black, 0.5) },
    },
    "&::-webkit-scrollbar-corner": { backgroundColor: "transparent" },
  };
}

/**
 * Adds that scrollbar to the table's own container props, underneath whatever the table already says
 * about its container — so a table that pins its own `overflowX`, height or position keeps all of it
 * and only gains the handle.
 */
function withVisibleScrollbar<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  const containerProps = baseOptions.muiTableContainerProps;
  return (containerArgs: { table: MRT_TableInstance<TData> }): TableContainerProps => {
    const inherited = parseFromValuesOrFunc(containerProps, containerArgs) ?? {};
    const inheritedSx = inherited.sx ?? {};
    return {
      ...inherited,
      sx: ((theme: Theme) => ({
        ...visibleScrollbarStyles(theme),
        ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
      })) as TableContainerProps["sx"],
    };
  };
}

export function MRTNoMemo<TData extends MRT_RowData>(props: Readonly<MRTNoMemoProps<TData>>) {
  "use no memo";

  const baseOptions = props.options ?? ({} as MRT_TableOptions<TData>);
  const {
    displayColumnDefOptions,
    muiFilterTextFieldProps,
    muiSearchTextFieldProps,
  } = baseOptions;
  const shouldUseMuiV9RowSelectColumn = Boolean(baseOptions.enableRowSelection);
  const withRowSelectColumn = shouldUseMuiV9RowSelectColumn
    ? {
        ...displayColumnDefOptions,
        "mrt-row-select": {
          Cell: ({ row, staticRowIndex, table }: MrtSelectCellArgs<TData>) => (
            <MrtSelectCheckbox row={row} staticRowIndex={staticRowIndex} table={table} />
          ),
          Header:
            baseOptions.enableSelectAll !== false && baseOptions.enableMultiRowSelection !== false
              ? ({ table }: { table: MRT_TableInstance<TData> }) => <MrtSelectCheckbox table={table} />
              // Named for screen readers and invisible to everyone else, exactly as the actions
              // column is. Left to MRT this falls back to its own "Select" text, which in a 40px
              // column arrives clipped to "Selec" — a table that turns select-all off should show an
              // empty header there, not a broken word.
              : RowSelectColumnHeader,
          ...displayColumnDefOptions?.["mrt-row-select"],
        },
      }
    : displayColumnDefOptions;
  const tableOptions: MRT_TableOptions<TData> = {
    ...baseOptions,
    // Every table's actions column is presented the same way, so it is applied here rather than
    // restated in seventeen files: hidden header, centred controls, and no more width than the
    // controls need. The table's own options are spread after, so anything it sets still wins.
    displayColumnDefOptions: baseOptions.enableRowActions
      ? {
          ...withRowSelectColumn,
          "mrt-row-actions": {
            Header: RowActionsColumnHeader,
            minSize: oneCollapsedButton,
            size: oneCollapsedButton,
            ...withRowSelectColumn?.["mrt-row-actions"],
          },
        }
      : withRowSelectColumn,
    ...(baseOptions.enableRowActions
      ? {
          muiTableBodyCellProps: withRowActionsCellProps(baseOptions),
          muiTableHeadCellProps: withRowActionsHeadCellProps(baseOptions),
        }
      : {}),
    muiTableProps: withEmptyTableProps(baseOptions),
    muiTableContainerProps: withVisibleScrollbar(baseOptions),
    muiFilterTextFieldProps:
      typeof muiFilterTextFieldProps === "function"
        ? (filterProps) => withMrtTextFieldRootSlot(muiFilterTextFieldProps(filterProps))
        : withMrtTextFieldRootSlot(muiFilterTextFieldProps),
    muiSearchTextFieldProps:
      typeof muiSearchTextFieldProps === "function"
        ? (searchProps) => withMrtTextFieldRootSlot(muiSearchTextFieldProps(searchProps))
        : withMrtTextFieldRootSlot(muiSearchTextFieldProps),
  };
  const baseTable = useMaterialReactTable<TData>(tableOptions);

  return <MaterialReactTable table={baseTable} />;
}
